import React, { useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";
import { API, defaultLimitPagination, get } from "../../../config";
import Spin from "../../common/Spin";
import Pagination from "../../common/Pagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEye } from "@fortawesome/free-solid-svg-icons";
import ImportLogDetail from "./ImportLogDetail";
import { debounce } from "underscore";
import NotImportedLogEdit from "./NotImportedLogEdit";
import moment from "moment";
import SearchBox from "../../common/SearchBox";

const NotImportedLogs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [notImportLogs, setNotImportLogs] = useState({});
  const [loading, setLoading] = useState(false);
  const [notImportLogId, setNotImportLogId] = useState();
  const [importLogModal, setImportLogModal] = useState(false);
  const search = query.get("search");
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");

  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const params = useParams();
  const projectId = params?.project_id;
  const trustType = params?.trust_type;
  const trust_type = params?.trust_type === "project" ? 1 : 0;
  const [editModal, setEditModal] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();

  const toggleEdit = (id) => {
    if (!editModal) {
      setNotImportLogId(id);
    } else {
      setNotImportLogId();
    }
    setEditModal(!editModal);
  };

  const toggleShowModal = () => {
    setImportLogModal(!importLogModal);
  };

  const handleDetails = (id) => {
    setNotImportLogId(id);
    setImportLogModal(true);
  };

  // *API Call for Import Logs List - Start
  const fetchNotImportedLogs = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.COA_IMPORT_LOGS}?builder_project_id=${
            projectId ? projectId : ""
          }&trust_type=${trust_type}&limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&status=0&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }`
        );
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        setTotal(data?.data?.total);
        setNotImportLogs(data?.data?.data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, trust_type, searchParam, sortField, sortOrder]
  );

  useEffect(() => {
    fetchNotImportedLogs();
  }, [fetchNotImportedLogs]);
  // *API Call for Import Logs List - End

  useEffect(() => {
    navigate(
      `/projects/${projectId}/chart-of-account-import-logs?tab=not-imported&limit=${
        limit ? limit : defaultLimitPagination
      }&page=${page ? page : 1}&search=${searchParam ? searchParam : ""}`,
      { replace: true }
    );
  }, [navigate, projectId, page, limit, trustType, searchParam]);

  // const handleDelete = () => {
  //   fetchNotImportedLogs();
  // };

  const getVerifiedDateTime = (date) => {
    return moment(date).format("DD-MM-YYYY HH:mm:ss").toString();
  };

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  // *Import Logs List Table Row and Column Generation, Filtering and Sorting of Record, Select Row and Expand Row - Start
  const notImportLogsGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: notImportLogs[i]?.id,
        id: notImportLogs[i]?.id ? notImportLogs[i]?.id : "",
        name: notImportLogs[i]?.name ? `${notImportLogs[i]?.name}` : "",
        type: notImportLogs[i]?.type === 0 ? "Debit" : "Credit",
        tax_rate:
          notImportLogs[i]?.tax === 1
            ? "GST " +
              (notImportLogs[i]?.gst_percentage
                ? notImportLogs[i]?.gst_percentage
                : 0) +
              "%"
            : notImportLogs[i]?.tax === 2
            ? "BAS Excluded"
            : notImportLogs[i]?.tax === 3
            ? "GST on Imports"
            : "GST Excluded",
        account_type: notImportLogs[i]?.account_type
          ? notImportLogs[i]?.account_type
          : "",
        code: notImportLogs[i]?.code ? notImportLogs[i]?.code : "-",
        sub_code: notImportLogs[i]?.sub_code ? notImportLogs[i]?.sub_code : "-",
        accounting_code: notImportLogs[i]?.accounting_code
          ? notImportLogs[i]?.accounting_code
          : "-",
        import_from: notImportLogs[i]?.import_from
          ? notImportLogs[i]?.import_from
          : "BuildTrust",
        last_sync_date: notImportLogs[i]?.last_sync_date
          ? getVerifiedDateTime(notImportLogs[i]?.last_sync_date)
          : "-",
        action: (
          <>
            <FontAwesomeIcon
              icon={faEye}
              size="1x"
              onClick={() => handleDetails(notImportLogs[i]?.id)}
            />
            <>
              <span></span>&nbsp;&nbsp;/&nbsp;&nbsp;
              <FontAwesomeIcon
                icon={faEdit}
                size="1x"
                onClick={() => toggleEdit(notImportLogs[i]?.id)}
              />
            </>

            {/* <>
            <span></span>&nbsp;&nbsp;/&nbsp;&nbsp;
              <Link
                to={{
                  pathname: `/projects/${projectId}/chart-of-account-import-logs/edit`,
                  search: `?id=${notImportLogs[i]?.id}`,
                }}
              >
                <FontAwesomeIcon icon={faEdit} size="1x" />
              </Link>
            </> */}
            {/* <NotImportedDelete
              handleDelete={handleDelete}
              id={notImportLogs[i]?.id}
            /> */}
          </>
        ),
      });
    }
    return items;
  };

  const notImportLogsData = notImportLogsGenerator(notImportLogs?.length);

  const columns = [
    {
      dataField: "name",
      text: "Name",
      sort: true,
    },
    {
      dataField: "code",
      text: "Code",
    },
    {
      dataField: "sub_code",
      text: "Sub Code",
    },
    {
      dataField: "account_type",
      text: "Account Type",
      sort: true,
    },
    {
      dataField: "type",
      text: "Type",
    },
    {
      dataField: "tax_rate",
      text: "Tax Rate",
    },
    {
      dataField: "accounting_code",
      text: "Accounting Code",
      sort: true,
    },
    {
      dataField: "import_from",
      text: "Import From",
    },
    {
      dataField: "last_sync_date",
      text: "Last synchronised time",
    },
    {
      dataField: "action",
      text: "Action",
      className: "text-center",
      style: { width: "97px" },
    },
  ];

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchNotImportedLogs(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchNotImportedLogs(limit, page);
  };

  // *Import Logs List Table Row and Column Generation, Filtering and Sorting of Record, Select Row and Expand Row - End

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  return (
    <>
      <div className="table-top-btn">
        <SearchBox
          placeholder={"Search Not Imported Logs"}
          searchParamData={searchParamData}
          handleSearchChange={handleSearchChange}
          handleClearSearch={handleClearSearch}
        />
      </div>
      <div className="audit-statement">
        {loading && <Spin />}
        <BootstrapTable
          keyField="batch_id"
          remote
          data={notImportLogsData}
          columns={columns}
          noDataIndication="No Data Found"
          onTableChange={handleTableChange}
        />

        <Pagination
          total={total}
          limit={parseInt(limit)}
          currentPage={page}
          updateLimit={handleUpdateLimit}
          updatePage={handleChangePage}
          from={from}
          to={to}
        />
      </div>

      {importLogModal && (
        <ImportLogDetail
          toggleShowModal={toggleShowModal}
          importLogId={notImportLogId}
          importLogModal={importLogModal}
          projectId={projectId}
        />
      )}

      {editModal && (
        <NotImportedLogEdit
          logId={notImportLogId}
          projectId={projectId}
          toggleEditModal={toggleEdit}
          fetchNotImportedLogs={() => fetchNotImportedLogs(limit, page)}
          editModal={editModal}
        />
      )}
    </>
  );
};

export default NotImportedLogs;
